import type { AdvancedFilterResponseItem } from 'types/api/advancedFilter';
import type { InternalTransaction } from 'types/api/internalTransaction';
import type { SearchResultItem } from 'types/api/search';
import type { TokenInfo } from 'types/api/token';
import type { TokenTransfer } from 'types/api/tokenTransfer';
import type { Transaction } from 'types/api/transaction';

import { EXCLUDED_CONTRACT_ADDRESSES } from './constants';

const EXCLUDED_ADDRESS_SET = new Set(
  EXCLUDED_CONTRACT_ADDRESSES.map((address) => address.toLowerCase()),
);

export function normalizeAddressHash(hash: string | null | undefined): string | null {
  if (!hash) {
    return null;
  }

  return hash.toLowerCase();
}

export function isExcludedAddressHash(hash: string | null | undefined): boolean {
  const normalized = normalizeAddressHash(hash);
  return normalized !== null && EXCLUDED_ADDRESS_SET.has(normalized);
}

export function isExcludedToken(token: Pick<TokenInfo, 'address'> | null | undefined): boolean {
  return isExcludedAddressHash(token?.address);
}

export function isExcludedTransaction(tx: Transaction): boolean {
  if (
    isExcludedAddressHash(tx.from.hash) ||
    isExcludedAddressHash(tx.to?.hash) ||
    isExcludedAddressHash(tx.created_contract?.hash)
  ) {
    return true;
  }

  return Boolean(
    tx.token_transfers?.some((transfer) => isExcludedToken(transfer.token)),
  );
}

export function isExcludedTokenTransfer(transfer: TokenTransfer): boolean {
  return (
    isExcludedToken(transfer.token) ||
    isExcludedAddressHash(transfer.from.hash) ||
    isExcludedAddressHash(transfer.to.hash)
  );
}

export function isExcludedInternalTransaction(tx: InternalTransaction): boolean {
  return (
    isExcludedAddressHash(tx.from.hash) ||
    isExcludedAddressHash(tx.to?.hash) ||
    isExcludedAddressHash(tx.created_contract?.hash)
  );
}

export function isExcludedAdvancedFilterItem(item: AdvancedFilterResponseItem): boolean {
  return (
    isExcludedAddressHash(item.from.hash) ||
    isExcludedAddressHash(item.to?.hash) ||
    isExcludedAddressHash(item.created_contract?.hash) ||
    isExcludedToken(item.token)
  );
}

export function isExcludedSearchResultItem(item: SearchResultItem): boolean {
  switch (item.type) {
    case 'token':
      return isExcludedAddressHash(item.address);
    case 'address':
    case 'contract':
    case 'label':
    case 'metadata_tag':
    case 'ens_domain':
      return isExcludedAddressHash(item.address);
    case 'transaction':
      return false;
    default:
      return false;
  }
}

export function filterTransactions<T extends Transaction>(items: Array<T>): Array<T> {
  return items.filter((item) => !isExcludedTransaction(item));
}

export function filterTokenTransfers<T extends TokenTransfer>(items: Array<T>): Array<T> {
  return items.filter((item) => !isExcludedTokenTransfer(item));
}

export function filterInternalTransactions<T extends InternalTransaction>(items: Array<T>): Array<T> {
  return items.filter((item) => !isExcludedInternalTransaction(item));
}
